import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useCurrentUser, type CurrentUser } from "@/lib/auth";
import { readUserHomePage } from "@/lib/userHomePage";
import {
  fetchStoredUserUiPreferences,
  isSafeAppHref,
  listsEqual,
  loadUserUiPreferences,
  readLocalFavorites,
  rememberUserUiPreferences,
  saveUserUiPreferences,
  type UserUiPreferences,
} from "@/lib/userUiPreferences";

interface Draft {
  homePage: string;
  favoriteHrefs: string[];
}

interface UserUiPreferencesContextValue {
  homePage: string;
  favoriteHrefs: string[];
  ready: boolean;
  setHomePage: (href: string) => void;
  toggleFavorite: (href: string) => void;
}

const UserUiPreferencesContext = createContext<UserUiPreferencesContextValue | null>(null);

function cloneDraft(draft: Draft): Draft {
  return { homePage: draft.homePage, favoriteHrefs: [...draft.favoriteHrefs] };
}

function sameDraft(left: Draft, right: Draft): boolean {
  return left.homePage === right.homePage && listsEqual(left.favoriteHrefs, right.favoriteHrefs);
}

function toggleHref(hrefs: string[], href: string): string[] {
  return hrefs.includes(href) ? hrefs.filter((item) => item !== href) : [...hrefs, href];
}

function draftFromPreferences(prefs: UserUiPreferences): Draft {
  return { homePage: prefs.home_page, favoriteHrefs: [...prefs.favorite_hrefs] };
}

export function UserUiPreferencesProvider({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser();
  const userRef = useRef<CurrentUser | null>(user);
  const draftRef = useRef<Draft>({ homePage: "/", favoriteHrefs: [] });
  const confirmedRef = useRef<Draft>({ homePage: "/", favoriteHrefs: [] });
  const readyRef = useRef(false);
  const savingRef = useRef(false);
  const pendingHomeRef = useRef<string | null>(null);
  const pendingTogglesRef = useRef<string[]>([]);
  const publishRef = useRef<() => void>(() => undefined);

  const [homePage, setHomePageState] = useState("/");
  const [favoriteHrefs, setFavoriteHrefs] = useState<string[]>([]);
  const [ready, setReady] = useState(false);

  userRef.current = user;

  const applyDraft = useCallback((draft: Draft) => {
    draftRef.current = cloneDraft(draft);
    setHomePageState(draft.homePage);
    setFavoriteHrefs(draft.favoriteHrefs);
  }, []);

  const publish = useCallback(() => {
    const currentUser = userRef.current;
    if (!currentUser || !readyRef.current || savingRef.current) return;
    const snapshot = cloneDraft(draftRef.current);
    if (sameDraft(snapshot, confirmedRef.current)) return;
    savingRef.current = true;
    void saveUserUiPreferences(currentUser, {
      home_page: snapshot.homePage,
      favorite_hrefs: snapshot.favoriteHrefs,
    })
      .then((saved) => {
        savingRef.current = false;
        const confirmed = draftFromPreferences(saved);
        confirmedRef.current = confirmed;
        if (!sameDraft(draftRef.current, snapshot)) {
          publishRef.current();
          return;
        }
        applyDraft(confirmed);
      })
      .catch(() => {
        savingRef.current = false;
        applyDraft(confirmedRef.current);
      });
  }, [applyDraft]);

  publishRef.current = publish;

  useEffect(() => {
    if (!user) {
      readyRef.current = false;
      setReady(false);
      return;
    }
    let cancelled = false;
    const localDraft: Draft = {
      homePage: readUserHomePage(user),
      favoriteHrefs: readLocalFavorites(user).filter(isSafeAppHref),
    };
    pendingHomeRef.current = null;
    pendingTogglesRef.current = [];
    readyRef.current = false;
    confirmedRef.current = cloneDraft(localDraft);
    applyDraft(localDraft);
    setReady(false);

    loadUserUiPreferences(user)
      .then((prefs) => {
        if (cancelled) return;
        let favoriteHrefsNext = prefs.favorite_hrefs;
        for (const href of pendingTogglesRef.current) {
          favoriteHrefsNext = toggleHref(favoriteHrefsNext, href);
        }
        pendingTogglesRef.current = [];
        const next: Draft = {
          homePage: pendingHomeRef.current ?? prefs.home_page,
          favoriteHrefs: favoriteHrefsNext,
        };
        pendingHomeRef.current = null;
        confirmedRef.current = draftFromPreferences(prefs);
        applyDraft(next);
        readyRef.current = true;
        setReady(true);
        publishRef.current();
      })
      .catch(() => {
        if (cancelled) return;
        readyRef.current = true;
        setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [user, applyDraft]);

  useEffect(() => {
    function onFocus() {
      const currentUser = userRef.current;
      if (!currentUser || !readyRef.current || savingRef.current) return;
      if (!sameDraft(draftRef.current, confirmedRef.current)) return;
      void fetchStoredUserUiPreferences()
        .then((prefs) => {
          if (!prefs.stored || savingRef.current) return;
          if (!sameDraft(draftRef.current, confirmedRef.current)) return;
          const next = draftFromPreferences(prefs);
          if (sameDraft(next, draftRef.current)) return;
          confirmedRef.current = next;
          applyDraft(next);
          rememberUserUiPreferences(currentUser, prefs);
        })
        .catch(() => undefined);
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [applyDraft]);

  const setHomePage = useCallback((href: string) => {
    if (!isSafeAppHref(href)) return;
    draftRef.current = { ...draftRef.current, homePage: href };
    setHomePageState(href);
    if (!readyRef.current) {
      pendingHomeRef.current = href;
      return;
    }
    publishRef.current();
  }, []);

  const toggleFavorite = useCallback((href: string) => {
    if (!isSafeAppHref(href)) return;
    const next = toggleHref(draftRef.current.favoriteHrefs, href);
    draftRef.current = { ...draftRef.current, favoriteHrefs: next };
    setFavoriteHrefs(next);
    if (!readyRef.current) {
      pendingTogglesRef.current.push(href);
      return;
    }
    publishRef.current();
  }, []);

  return (
    <UserUiPreferencesContext.Provider
      value={{ homePage, favoriteHrefs, ready, setHomePage, toggleFavorite }}
    >
      {children}
    </UserUiPreferencesContext.Provider>
  );
}

export function useUserUiPreferences(): UserUiPreferencesContextValue {
  const context = useContext(UserUiPreferencesContext);
  if (!context) {
    throw new Error("useUserUiPreferences must be used within UserUiPreferencesProvider");
  }
  return context;
}
